import { Component, Vue, Watch } from 'vue-property-decorator';
import { isEqual } from 'lodash';

import AnalysisStep from '@/components/analysis/steps/AnalysisStep.vue';
import ElementUpdateSoles from '@/components/elements/dfu/ElementUpdateSoles.vue';

import { StepsKeys } from '@/plugins/podolib/services/analysis';
import { TEntity, TFetch } from '@/plugins/podolib/services/entities/Entity';
import { TStation } from '@/plugins/podolib/services/entities/EntityStations';
import { TSoles } from '@/plugins/podolib/services/entities/EntitySoles';
import { Severity } from '@sentry/types';
import { StepStation } from '@/components/analysis/steps/station/AnalysisStepStation';
import {
  AnalysisRunningMode,
  AnalysisWalkingMode,
  AnalysisType,
} from '@/components/analysis/steps/type/AnalysisStepType';
import { lt } from 'semver';

type CheckStatus = {
  pending: boolean;
  timeout: any;
  error: Error | null;
};

export type StepSolesPair = {
  solesPairId: TEntity<TSoles>['_id'] | null;
};

@Component({
  components: {
    AnalysisStep,
    ElementUpdateSoles,
  },
})
export default class AnalysisStepSolesPair extends Vue {
  // Main data payload
  data: StepSolesPair = null!;

  check: CheckStatus = {
    pending: false,
    timeout: null,
    error: null,
  };

  cacheStationId: TEntity<TStation>['_id'] | null = null;

  /**
   * Helper for giving ability to use types and enums in DOM
   */
  tsdom = {
    Type: AnalysisType,
    RunningMode: AnalysisRunningMode,
    WalkingMode: AnalysisWalkingMode,
  };

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Get manifest entry
   */
  get stepSolesPair() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.SolesPair);
  }

  /**
   * Manifest entry of "station" step
   */
  get stepStation() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Station);
  }

  /**
   * Get 'solesPairs' entity
   */
  get solesPairs() {
    return this.$pdc.analysis!.entitySolesPairs;
  }

  /**
   * Get 'patient' entity
   */
  get patient() {
    return this.$pdc.analysis!.entityPatient;
  }

  get selectedSolesPair() {
    return this.solesPairs!.data.find((solesPair) => solesPair._id === this.data.solesPairId);
  }

  /**
   * Get if selected pair of soles is checked
   */
  get selectedSolesPairIsChecked() {
    return this.selectedSolesPair?.checked;
  }

  /**
   * Get updatable soles
   */
  get getUpdatableSoles() {
    return this.$store.getters['pdc/entities/soles/getUpdatableSoles'];
  }

  /**
   * Get if we display abbr
   * Check footsize doublon
   */
  get displayAbbr() {
    const _b: number[] = [];
    for (let index = 0; index < this.solesPairs!.data.length; index++) {
      let solesPair = this.solesPairs!.data[index];
      if (index && _b.includes(solesPair.left.footSize)) {
        return true;
      }
      _b.push(solesPair.left.footSize);
    }
    return false;
  }

  /**
   * Manifest entry of "type" step
   */
  get stepType() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Type);
  }

  /**
   * If there are not operational soles
   */
  get solesNotOperational(): boolean {
    return !!this.solesPairs?.data?.filter((solesPair) => !this.isSolesOperational(solesPair._id)).length;
  }
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Keep manifest up to date
   */
  @Watch('data', { deep: true })
  onDataChanged(newValue: StepSolesPair) {
    if (!isEqual(newValue, this.stepSolesPair)) {
      this.$pdc.analysis!.setManifestEntry(StepsKeys.SolesPair, this.data);

      if (this.stepSolesPair.__system.touched && this.stepSolesPair.solesPairId) {
        // Analytics
        this.$pdc.analytics?.actions.trackSuccess('NewAnalysis SolesSelected', {
          solesPairId: this.stepSolesPair.solesPairId,
          leftBatteryLevel: this.selectedSolesPair!.left.battery,
          rightBatteryLevel: this.selectedSolesPair!.right.battery,
        });
      }
    }
  }

  /**
   * Keep local data up to date
   */
  @Watch('stepSolesPair', { deep: true, immediate: true })
  onStepComputeChanged(newValue: StepSolesPair) {
    if (!isEqual(newValue, this.data)) {
      this.data = { ...this.data, ...this.stepSolesPair };
    }
  }

  /**
   * Watch 'station' step
   */
  @Watch('stepStation', { deep: true })
  async onStepStationChanged(newValue: StepStation, oldValue: StepStation) {
    if (newValue.stationId && newValue.stationId !== oldValue.stationId) {
      await this.checkStatus();
    }
  }

  /**
   * Watch 'solesPairs' entity
   */
  @Watch('solesPairs', { deep: true })
  async onSolesPairsChanged(newValue: TFetch<TEntity<TSoles>>, oldValue: TFetch<TEntity<TSoles>>) {
    if (newValue.data.length > oldValue.data.length) {
      await this.checkStatus();
    }
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Clear check status
   */
  clearCheckStatusTimeout() {
    if (this.check.timeout) clearTimeout(this.check.timeout);
  }

  /**
   * Complete check status
   */
  completeCheckStatus() {
    // Clear check status
    this.clearCheckStatusTimeout();
    // Reset 'pending' flag
    this.check.pending = false;

    if (this.data.solesPairId && !this.selectedSolesPairIsChecked) {
      this.data.solesPairId = null;
    }
  }

  /**
   * Check status
   */
  async checkStatus() {
    // Avoid duplication of the process
    if (this.check.pending) return;

    // Reset flags
    this.check.error = null;
    // Up 'pending' flag
    this.check.pending = true;

    // Set stationId into cache
    this.cacheStationId = this.stepStation!.stationId;

    // Clear process states
    this.clearCheckStatusTimeout();

    // Remove 'checked' status
    this.clearStatus();

    try {
      // Launch scan process in station
      await this.$pdc.entities!.soles.getBatteries();

      this.check.timeout = setTimeout(this.completeCheckStatus, 3000); // -> 3sec

      // Set timeout
    } catch (e) {
      // Set 'error'
      this.check.error = e;
      // Down 'pending' flag
      this.check.pending = false;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    }
  }

  /**
   * Remove 'checked' status
   */
  clearStatus() {
    // Remove pairs of soles' 'checked' flag
    this.solesPairs!.data = this.solesPairs!.data.map((x) => {
      const { checked, ...sole } = x;
      return sole;
    }) as TEntity<TSoles>[];
  }

  /**
   * Check if a pair of soles have the good size for the current patient
   */
  recommendedFootSize(size: string) {
    return [this.patient!.infos.footSize, this.patient!.infos.footSize + 1].includes(parseInt(size));
  }

  /**
   * Compute battery color
   */
  batteryColor(value: number) {
    switch (true) {
      case value <= 25:
        return 'error';
      case value >= 25 && value <= 50:
        return 'warning';
      case value >= 50:
        return 'success';
      default:
        return 'grey';
    }
  }

  /**
   * Convert a value (0 - 100) to an icon of battery
   */
  batteryIcon(value: number) {
    switch (true) {
      case value >= 10 && value <= 25:
        return '$vuetify.battery-quarter--d';
      case value >= 25 && value <= 50:
        return '$vuetify.battery-half--d';
      case value >= 50 && value <= 75:
        return '$vuetify.battery-three-quarters--d';
      case value >= 75 && value <= 100:
        return '$vuetify.battery-full--d';
      default:
        return '$vuetify.battery-empty--d';
    }
  }

  /**
   * Convert a value (0 - 100) to an icon of battery
   */
  isSolesOperational(id: string) {
    const soles = this.solesPairs!.data.find((x: any) => x._id === id);
    if (soles) {
      const isRunAnalysis = this.stepType.type === AnalysisType.Running;
      const isResearchMode = this.stepType.mode === AnalysisWalkingMode.Research;
      const maj = soles.left.firmware.maj;
      const min = soles.left.firmware.min;
      const build = soles.left.firmware.build;

      // If soles aren't calibrated
      /*if (!soles.calibrated && isRunAnalysis)  return {
        reason: 'If soles aren t calibrated - running',
        status: false
      };*/

      // If soles aren't updated
      // If is under the 2.7.6 version
      if (soles.updatable && lt(`${maj}.${min}.${build}`, '2.7.6')) return false;
      // if (soles.updatable && maj === 2 && min === 6 && (isRunAnalysis || isResearchMode)) return false;
      // if (soles.updatable && maj === 2 && min === 6 && !isRunAcquisition) return true;

      return true;
    }
    return false;
  }

  /**
   * @deprecated
   */
  openUpdateDialog() {
    (this.$refs.update as any).open();
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Emit "next" event to the parent
   */
  next() {
    this.$emit('next');
  }

  /**
   * On mounted
   * - check status
   */
  async mounted() {
    await this.checkStatus();
  }

  /**
   * On beforeDestroy
   * - clear check status
   */
  beforeDestroy() {
    this.clearCheckStatusTimeout();
  }
}
